import React, { useState } from "react";
import { Mail, Linkedin, Send, CheckCircle, AlertCircle } from "lucide-react";
import axios from "axios";
import { profileData } from "../mock";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    if (error) setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      setError("Something went wrong. Please try emailing me directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-16 md:py-24" style={{ background: "#09090B" }}>
      <div className="max-w-6xl mx-auto px-5 sm:px-6">
        {/* Section header */}
        <div className="mb-14">
          <p className="text-xs font-semibold uppercase tracking-widest text-[#06B6D4] mb-3">Connect</p>
          <h2 className="text-3xl md:text-4xl font-bold text-white">Get in Touch</h2>
          <p className="text-[#71717A] mt-3 max-w-lg">
            Open to conversations about product, growth, or anything interesting. Don't hesitate to reach out.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left: Info */}
          <div className="space-y-6">
            <a
              href={`mailto:${profileData.email}`}
              className="flex items-center gap-4 p-5 rounded-xl border group hover:border-[#06B6D4]/40 transition-all duration-300"
              style={{ background: "#0F0F11", border: "1px solid #1E1E24" }}
            >
              <div
                className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: "rgba(6,182,212,0.1)", border: "1px solid rgba(6,182,212,0.3)" }}
              >
                <Mail className="w-5 h-5 text-[#06B6D4]" />
              </div>
              <div>
                <p className="text-xs text-[#71717A] mb-0.5">Email</p>
                <p className="text-white font-medium text-sm group-hover:text-[#06B6D4] transition-colors duration-200">
                  {profileData.email}
                </p>
              </div>
            </a>

            <a
              href={profileData.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 p-5 rounded-xl border group hover:border-[#06B6D4]/40 transition-all duration-300"
              style={{ background: "#0F0F11", border: "1px solid #1E1E24" }}
            >
              <div
                className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: "rgba(6,182,212,0.1)", border: "1px solid rgba(6,182,212,0.3)" }}
              >
                <Linkedin className="w-5 h-5 text-[#06B6D4]" />
              </div>
              <div>
                <p className="text-xs text-[#71717A] mb-0.5">LinkedIn</p>
                <p className="text-white font-medium text-sm group-hover:text-[#06B6D4] transition-colors duration-200">
                  aditya-sonthalia
                </p>
              </div>
            </a>
          </div>

          {/* Right: Contact form */}
          <div
            className="p-6 rounded-xl border"
            style={{ background: "#0F0F11", border: "1px solid #1E1E24" }}
          >
            {submitted ? (
              <div className="flex flex-col items-center justify-center h-full min-h-[240px] gap-4">
                <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: "rgba(16,185,129,0.1)" }}>
                  <CheckCircle className="w-7 h-7 text-[#10B981]" />
                </div>
                <p className="text-white font-semibold text-lg">Message Sent!</p>
                <p className="text-[#71717A] text-sm text-center">Thanks for reaching out. I'll get back to you soon.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs text-[#71717A] mb-1.5 font-medium">Name</label>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Your name"
                    className="w-full px-4 py-3 rounded-md text-sm text-white placeholder-[#3F3F46] outline-none focus:ring-1 focus:ring-[#06B6D4] transition-all duration-200"
                    style={{ background: "#141416", border: "1px solid #27272A" }}
                  />
                </div>
                <div>
                  <label className="block text-xs text-[#71717A] mb-1.5 font-medium">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    className="w-full px-4 py-3 rounded-md text-sm text-white placeholder-[#3F3F46] outline-none focus:ring-1 focus:ring-[#06B6D4] transition-all duration-200"
                    style={{ background: "#141416", border: "1px solid #27272A" }}
                  />
                </div>
                <div>
                  <label className="block text-xs text-[#71717A] mb-1.5 font-medium">Message</label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    placeholder="What's on your mind?"
                    rows={4}
                    className="w-full px-4 py-3 rounded-md text-sm text-white placeholder-[#3F3F46] outline-none focus:ring-1 focus:ring-[#06B6D4] transition-all duration-200 resize-none"
                    style={{ background: "#141416", border: "1px solid #27272A" }}
                  />
                </div>
                {error && (
                  <div className="flex items-center gap-2 text-xs text-red-400 p-3 rounded-md" style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)" }}>
                    <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                    {error}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 rounded-md text-sm font-semibold text-[#09090B] bg-[#06B6D4] hover:bg-[#0891B2] transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                      </svg>
                      Sending...
                    </span>
                  ) : (
                    <><Send className="w-4 h-4" /> Send Message</>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
